/**
 * QUOTATION DETAIL — dettaglio del preventivo, layout a tre colonne.
 *
 * Sinistra: stato, "Info chiave" interamente inline-editabile, note, azioni
 * rapide. Centro: attività. Destra: relazioni, documenti, chiusura.
 *
 * ## "Info chiave" è una LISTA ORDINATA, non JSX fisso
 *
 * È la condizione perché i campi dei plugin si **intercalino** fra quelli core
 * invece di accodarsi: ogni campo core dichiara il proprio `order`, i
 * contributi in slot `detail-left:info` dichiarano il loro, e il render li
 * fonde e ordina. Senza, un plugin potrebbe solo aggiungere in fondo, e nessun
 * layout che alterni campi propri e campi core sarebbe riproducibile.
 *
 * ## Cliente: il dominio sono le aziende COLLEGATE
 *
 * Il lookup del cliente non interroga il server: le aziende nei pannelli
 * relazione a destra sono l'insieme dei valori ammessi, e questa combo ne
 * sceglie uno. Non sono due viste dello stesso dato ma insieme ed elemento.
 * Corollario: `businessId` non può puntare a un'azienda non collegata, e se
 * quella scelta viene scollegata dal pannello il campo si svuota.
 */

import { useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { formatCurrency, formatFileSize } from '@crm/shared';
import { QuotationsGlyph, FolderGlyph } from '../../components/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ActivityTimeline } from '../../components/ActivityTimeline';
import { DetailTabs } from '../../components/DetailTabs';
import { isValidActivitySection } from '../../components/activitySections';
import { useActivityFocus } from '../../components/useActivityFocus';
import { useEntityActivities } from '../../components/useEntityActivities';
import { EditableField, type EditableValue, type LookupOption } from '../../components/EditableField';
import { InfoLine } from '../../components/InfoLine';
import { RelationPanels } from '../../components/RelationPanels';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import { useFileDropzone } from '../../components/useFileDropzone';
import { useSlotContributors, SLOT_ORDER_LAST } from '../../plugin/useSlotContributors';
import { vocabularyLabelKey } from '../../plugin/vocabularyRegistry';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { selectRelationshipsBySource } from '../relationship/relationshipSlice';
import { useRelationshipsSource } from '../relationship/useRelationshipsSource';
import { fetchRelationships } from '../relationship/relationshipThunks';
import { businessApi } from '../business/businessApi';
import { fetchBusinesses } from '../business/businessThunks';
import type { BusinessState } from '../business/businessTypes';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import { pipelinesOfType } from '../pipeline/pipelineEntityTypes';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { findOwningPipeline, pipelineStateLabel, sortedStates } from '../pipeline/pipelineUtils';
import TaskForm from '../task/TaskForm';
import { useAttachmentDownload } from '../message/useAttachmentDownload';
import { ACTIVITY_TO_TASK_TYPE, type TaskType } from '../task/taskTypes';
import { fetchQuotation, updateQuotation } from './quotationThunks';
import { clearCurrentQuotation } from './quotationSlice';
import { AddNoteDialog } from './AddNoteDialog';
import { fetchQuotationFiles, deleteQuotationFile } from '../quotationFile/quotationFileThunks';
import { clearQuotationFiles } from '../quotationFile/quotationFileSlice';
import { makeQuotationFileUploader } from '../quotationFile/quotationFileUploader';
import type { QuotationFile } from '../quotationFile/quotationFileMappers';
import type { QuotationFileState } from '../quotationFile/quotationFileTypes';
import {
  QUOTATION_STATUSES,
  quotationStatusChipClass,
  type QuotationState,
  type QuotationUpdatePayload,
} from './quotationTypes';

export default function QuotationDetail() {
  const { id, section, activityId } = useParams<{
    id: string;
    section?: string;
    activityId?: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirmDialog();

  const { currentItem: quotation } = useAppSelector(
    (state) => state.quotations as QuotationState,
  );
  const { items: files } = useAppSelector(
    (state) => state.quotationFiles as QuotationFileState,
  );
  const { items: businesses } = useAppSelector((state) => state.businesses as BusinessState);
  const { items: allPipelines } = useAppSelector((state) => state.pipelines as PipelineSliceState);

  const [quickDrawer, setQuickDrawer] = useState<{ initialType?: TaskType } | null>(null);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const numId = id ? Number(id) : 0;

  const infoContributors = useSlotContributors('Quotation', 'detail-left:info');
  const leftAfterContributors = useSlotContributors('Quotation', 'detail-left:after');
  const rightTopContributors = useSlotContributors('Quotation', 'detail-right:top');
  const rightBottomContributors = useSlotContributors('Quotation', 'detail-right:bottom');
  const leftBottomContributors = useSlotContributors('Quotation', 'detail-left:bottom');

  // Upload allegati dal bottone "Carica file". Del hook si usa il solo flusso
  // click-only: il bottone è il trigger, non c'è drop-zone. Nessun validatore,
  // gli allegati sono liberi.
  const attachDropzone = useFileDropzone({
    uploadFile: makeQuotationFileUploader(dispatch, numId, 'attachment'),
    successMessageKey: 'quotation.uploadFileToast',
    errorMessageKey: 'quotation.uploadFileError',
  });
  const activities = useEntityActivities('Quotation', numId);
  const downloadAttachment = useAttachmentDownload();

  // Allegati di TUTTE le email collegate al preventivo, appiattiti in una lista sola:
  // la card di destra e' un elenco di file, non di conversazioni. Ogni voce si porta
  // dietro l'oggetto della mail da cui viene, perche' un allegato senza la sua mail e'
  // un nome di file e basta.
  const mailAttachments = useMemo(
    () => activities.items.flatMap((item) =>
      (item.message?.attachments ?? []).map((attachment) => ({
        attachment,
        subject: item.title,
      }))),
    [activities.items],
  );

  // Eliminazione di un allegato. Il backend cancella anche il DLFileEntry
  // (QuotationFileLocalServiceImpl), quindi l'operazione non è recuperabile:
  // di qui la conferma esplicita prima di dispatchare.
  const askDeleteFile = useCallback(
    (file: QuotationFile) => {
      confirm.requestConfirm(
        t('quotation.deleteFileConfirm', { fileName: file.fileName }),
        async () => {
          try {
            await dispatch(deleteQuotationFile({
              quotationId: numId,
              quotationFileId: file.quotationFileId,
            })).unwrap();
            toast({ severity: 'success', message: t('common.saved') });
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        },
      );
    },
    [confirm, dispatch, numId, t, toast],
  );

  // Sotto-tab attività pilotati dall'URL: la fonte di verità del filtro è
  // l'URL, non lo stato interno di useEntityActivities.
  // Tab contribuite dai plugin per questa entita' (TS-107). Le legge la scheda e
  // non la timeline perche' servono anche qui sotto, alla guardia del redirect:
  // uno slug contribuito e' una sezione valida quanto 'notes'.
  const activityTabs = useSlotContributors('Quotation', 'detail-center:activity-tab');
  const focusItemId = useActivityFocus(section, activityId);
  const handleSectionChange = useCallback(
    (slug: string | undefined) => {
      navigate(slug ? `/quotations/${id}/${slug}` : `/quotations/${id}`);
    },
    [id, navigate],
  );

  // Slug sconosciuto -> redirect alla rotta base ("Tutte").
  useEffect(() => {
    if (section != null && !isValidActivitySection(section)
      && !activityTabs.some((c) => c.slug === section))
      navigate(`/quotations/${id}`, { replace: true });
  }, [section, id, navigate, activityTabs]);

  const quotationFqn = getEntityFqn('Quotation');

  // Le relazioni servono qui per il dominio del cliente e servono a
  // RelationPanels in colonna destra: l'hook è ref-contato, parte una sola GET.
  useRelationshipsSource(quotationFqn, numId);

  useEffect(() => {
    if (numId) {
      dispatch(fetchQuotation(numId));
      dispatch(fetchQuotationFiles(numId));
    }
    return () => {
      dispatch(clearCurrentQuotation());
      dispatch(clearQuotationFiles());
    };
  }, [numId, dispatch]);

  // Businesses in store per risolvere il displayLabel del lookup cliente.
  useEffect(() => {
    if (businesses.length === 0) dispatch(fetchBusinesses());
  }, [businesses.length, dispatch]);

  // Pipeline per il campo "Stato pipeline" (states embedded nella lista).
  useEffect(() => {
    // Sul totale NON filtrato: se il sito ha solo funnel di altre entita', l'elenco filtrato resta
    // vuoto e si rifarebbe la fetch a ogni render.
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  const relationships = useAppSelector(selectRelationshipsBySource(quotationFqn, numId));

  const businessFqn = getEntityFqn('Business');
  const linkedBusinessIds = useMemo(
    () => relationships.filter((r) => r.className2 === businessFqn).map((r) => r.classPK2),
    [relationships, businessFqn],
  );
  // Chiave stabile: l'array cambia identità a ogni render dello slice, e usarlo
  // come dipendenza rifarebbe la risoluzione dei nomi ogni volta.
  const linkedBusinessIdsKey = linkedBusinessIds.join(',');

  const [clientOptions, setClientOptions] = useState<LookupOption[]>([]);
  useEffect(() => {
    const ids = linkedBusinessIdsKey ? linkedBusinessIdsKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      setClientOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const options = await Promise.all(
        ids.map(async (bid) => {
          // Le aziende già in store evitano la GET; le altre si risolvono per
          // id. Sono poche — quelle collegate a un preventivo, non l'anagrafica.
          const cached = businesses.find((b) => b.businessId === bid);
          if (cached) return { id: bid, label: cached.name };
          try {
            const b = (await businessApi.getById(bid)) as { businessId: number; name: string };
            return { id: bid, label: b.name };
          } catch {
            return { id: bid, label: `#${bid}` };
          }
        }),
      );
      if (!cancelled) setClientOptions(options);
    })();
    return () => { cancelled = true; };
  }, [linkedBusinessIdsKey, businesses]);

  // Il picker non interroga il server: il dominio è locale e piccolo, quindi la
  // query filtra le opzioni già risolte.
  const fetchBusinessOptions = useCallback(
    async (query: string): Promise<LookupOption[]> => {
      const q = query.trim().toLowerCase();
      if (!q) return clientOptions;
      return clientOptions.filter((o) => o.label.toLowerCase().includes(q));
    },
    [clientOptions],
  );

  // Scollegando dal pannello a destra l'azienda selezionata, il campo cliente
  // si svuota: il valore non appartiene più al dominio, e lasciarlo mostrerebbe
  // una scelta non più rifacibile. Il guard sulla lunghezza evita di azzerare
  // al primo render, quando le relazioni non sono ancora arrivate.
  const currentBusinessId = quotation?.businessId ?? 0;
  useEffect(() => {
    if (!numId || currentBusinessId <= 0) return;
    const linked = linkedBusinessIdsKey ? linkedBusinessIdsKey.split(',').map(Number) : [];
    if (linked.length === 0) return;
    if (linked.includes(currentBusinessId)) return;

    void dispatch(updateQuotation({ quotationId: numId, businessId: 0, businessName: '' }))
      .unwrap()
      .catch(() => {
        // Best-effort: se fallisce il campo resta com'era e l'utente può
        // comunque sceglierne un altro fra i collegati.
      });
  }, [numId, currentBusinessId, linkedBusinessIdsKey, dispatch]);

  if (!quotation) return <p>{t('common.loading')}</p>;

  const commitField =
    (field: keyof QuotationUpdatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { quotationId: quotation.quotationId, [field]: newValue } as QuotationUpdatePayload;
        await dispatch(updateQuotation(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  // Commit speciale per businessId: risolve anche il businessName denormalizzato.
  const commitBusinessId = async (newValue: EditableValue) => {
    const newBusinessId = typeof newValue === 'number' ? newValue : Number(newValue);
    if (!newBusinessId) return;
    try {
      const business = (await businessApi.getById(newBusinessId)) as { businessId: number; name: string };
      await dispatch(updateQuotation({
        quotationId: quotation.quotationId,
        businessId: newBusinessId,
        businessName: business.name,
      })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  // Commit dello stato di pipeline: la combo lavora su value stringa, ma il
  // backend vuole un int (come commitBusinessId).
  const commitCurrentStateId = async (newValue: EditableValue) => {
    const stateId = typeof newValue === 'number' ? newValue : Number(newValue);
    try {
      await dispatch(updateQuotation({ quotationId: quotation.quotationId, currentStateId: stateId })).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  // Il campo "Stato pipeline" edita lo stato SOLO all'interno della pipeline che
  // già possiede il preventivo (spostare tra pipeline è materia del form/cascata).
  // Si ricava la pipeline proprietaria da currentStateId; se non c'è (preventivo
  // non assegnato) il campo resta in sola lettura.
  // TS-117: solo i funnel DEL PREVENTIVO. Lo slice e' condiviso e contiene anche quelli di altre
  // entita': senza il filtro, `findOwningPipeline` potrebbe attribuire il preventivo a un funnel che
  // non e' suo, se due stati di entita' diverse avessero lo stesso id.
  const pipelines = pipelinesOfType(allPipelines, quotationFqn);
  const hasPipelines = pipelines.length > 0;
  const owningPipeline = findOwningPipeline(pipelines, quotation.currentStateId);
  const pipelineStateOptions = sortedStates(owningPipeline).map((s) => ({
    value: String(s.pipelineStateId),
    label: pipelineStateLabel(s),
  }));

  // Campi core di "Info chiave" con il proprio `order`, fusi e ordinati con i
  // contributi dei plugin.
  //
  // La numerazione va di cento in cento, e non di uno in uno, per una ragione
  // sola: fra due campi core restano novantanove posizioni libere, così un
  // plugin può intercalare i propri senza che il core sappia nulla di lui e
  // senza che nessuno debba rinumerare. Il core non riserva intervalli a
  // nessuno — chi contribuisce sceglie il proprio valore e basta.
  const coreInfoFields: { key: string; order: number; node: ReactNode }[] = [
    {
      key: 'externalCode', order: 100,
      node: (
        <InfoLine label={t('quotation.externalCode')}>
          <EditableField
            value={quotation.externalCode ?? ''}
            variant={{ kind: 'text' }}
            readOnly
            onCommit={commitField('externalCode')}
            label={t('quotation.externalCode')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'title', order: 200,
      node: (
        <InfoLine label={t('quotation.titleLabel')}>
          <EditableField
            value={quotation.title}
            variant={{ kind: 'text' }}
            required
            onCommit={commitField('title')}
            label={t('quotation.titleLabel')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'business', order: 300,
      node: (
        <InfoLine label={t('quotation.clientName')}>
          <EditableField
            value={quotation.businessId}
            variant={{
              kind: 'lookup',
              fetcher: fetchBusinessOptions,
              // Priorità: businessName già risolto in state > lookup live su
              // businesses (Redux) > fallback "#id".
              displayLabel:
                quotation.businessName
                ?? businesses.find((b) => b.businessId === quotation.businessId)?.name
                ?? `#${quotation.businessId}`,
            }}
            required
            onCommit={commitBusinessId}
            label={t('quotation.clientName')}
          />
        </InfoLine>
      ),
    },
    // Stato pipeline (TS-101): editabile solo entro la pipeline proprietaria. La riga
    // compare solo se esistono pipeline; se il preventivo non è assegnato
    // (currentStateId = 0) resta in sola lettura — l'assegnazione si fa dal form.
    // Sta fra le informazioni chiave e non nella card identità, dove lo stato del
    // preventivo è già una pastiglia: sono due stati diversi e affiancarli li
    // farebbe leggere come alternative dello stesso campo.
    ...(hasPipelines ? [{
      key: 'pipelineState', order: 350,
      node: (
        <InfoLine label={t('quotation.pipelineStateLabel')}>
          {owningPipeline ? (
            <EditableField
              value={String(quotation.currentStateId)}
              variant={{ kind: 'select', options: pipelineStateOptions }}
              required
              onCommit={commitCurrentStateId}
              label={t('quotation.pipelineStateLabel')}
            />
          ) : (
            <EditableField
              value={t('quotation.pipelineStateUnassigned')}
              variant={{ kind: 'text' }}
              readOnly
              onCommit={commitCurrentStateId}
              label={t('quotation.pipelineStateLabel')}
            />
          )}
        </InfoLine>
      ),
    }] : []),
    {
      key: 'createDate', order: 400,
      node: (
        <InfoLine label={t('quotation.creationDate')}>
          <EditableField
            value={quotation.createDate.split('T')[0]}
            variant={{ kind: 'date' }}
            readOnly
            onCommit={async () => { /* readonly */ }}
            label={t('quotation.creationDate')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'dueDate', order: 500,
      node: (
        <InfoLine label={t('quotation.dueDate')}>
          <EditableField
            value={quotation.dueDate ? quotation.dueDate.split('T')[0] : ''}
            variant={{ kind: 'date' }}
            onCommit={commitField('dueDate')}
            label={t('quotation.dueDate')}
          />
        </InfoLine>
      ),
    },
    {
      key: 'amount', order: 600,
      node: (
        <InfoLine label={t('quotation.amount')}>
          <EditableField
            value={quotation.amount}
            variant={{ kind: 'number', min: 0 }}
            onCommit={commitField('amount')}
            label={t('quotation.amount')}
          />
        </InfoLine>
      ),
    },
  ];

  const infoRows = [
    ...coreInfoFields,
    ...infoContributors.map((c) => ({
      key: `plugin:${c.key}`,
      order: c.order ?? SLOT_ORDER_LAST,
      node: <c.component entityId={quotation.quotationId} />,
    })),
  ].sort((a, b) => a.order - b.order);

  // Tutto ciò che NON è il documento di preventivo. È un complemento e non un
  // elenco di tipi ammessi: con l'allowlist `type === 'attachment'` un file di
  // tipo contribuito da un plugin — il DXF di Trentino — non compariva in
  // nessuna delle due liste del dettaglio, quindi risultava caricato e
  // invisibile. Un vocabolario estendibile filtrato per enumerazione perde in
  // silenzio tutto quello che non era previsto quando il filtro è stato scritto.
  const attachments = files.filter((f) => f.type !== 'quotation');

  // Documenti: ogni file caricato sul preventivo, compresi quelli di un tipo
  // contribuito da un plugin. Fuori resta solo il documento di preventivo
  // (type='quotation'), che ha una collocazione propria. L'etichetta del tipo la
  // risolve il registro dei vocabolari, quindi un DXF si legge "Disegno DXF" e
  // non come la chiave grezza.
  //
  // ATTENZIONE: cancellare da qui un file di tipo contribuito può avere effetti
  // che il core non conosce — un DXF, per esempio, porta con sé tutte le righe
  // del preventivo (QuotationFileModelListener del plugin Trentino). La conferma
  // mostra il solo nome del file: se questi effetti devono essere detti
  // all'utente, serve un canale per farli dichiarare al plugin.
  //
  // Reso come i pannelli relazione (`.panel-box` -> `.rel-card` del mock):
  // stessa testata con conteggio, stesso pulsante tratteggiato in fondo. Nel
  // mock precede Contatti e Aziende, quindi sta in una const e viene inserito
  // PRIMA di <RelationPanels />.
  const documentsPanel = (
    <Accordion defaultExpanded disableGutters elevation={0} className="panel-box">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="relation-header">
          <Typography className="relation-title">{t('quotation.documentsHeading')}</Typography>
          <span className="relation-count">{attachments.length}</span>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="panel-box__details">
        {attachments.length === 0 ? (
          <p className="text-muted text-sm">{t('quotation.noDocuments')}</p>
        ) : (
          attachments.map((f) => (
            // `.doc-item` del mock: emblema in tinta secondaria, nome + tipo,
            // cestino rivelato all'hover come nelle altre liste.
            <div key={f.quotationFileId} className="doc-item">
              <span className="doc-emblem"><FolderGlyph /></span>
              <div className="doc-main">
                <div className="doc-name" title={f.fileName}>{f.fileName}</div>
                <div className="doc-meta">
                  {t('quotation.fileType', {
                    type: t(vocabularyLabelKey('quotationFileType', f.type)),
                    ext: f.extension,
                  })}
                </div>
              </div>
              <IconButton
                size="small"
                className="doc-del"
                aria-label={t('quotation.deleteFile')}
                onClick={() => askDeleteFile(f)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))
        )}
        {/* Stesso pulsante tratteggiato dei pannelli relazione: qui carica un
            file invece di collegare un record. Riusa la dropzone gia' montata
            per il bottone "Carica File" della colonna sinistra. */}
        <button
          type="button"
          className="rel-add"
          onClick={attachDropzone.onClick}
          disabled={attachDropzone.uploading}
        >
          {t('quotation.uploadFile')}
        </button>
      </AccordionDetails>
    </Accordion>
  );

  // Allegati delle email collegate (TS-108). Card gemella di Documenti, con due
  // differenze che vengono dalla natura del dato: non si caricano e non si
  // cancellano — appartengono alla mail, non al preventivo — quindi l'unica azione
  // e' scaricarli, e la riga dice DA QUALE mail arrivano.
  //
  // La fonte e' la timeline gia' in pagina: la proiezione bulk porta i metadati degli
  // allegati insieme alla riga del messaggio, quindi qui non c'e' nessuna fetch e la
  // card e' popolata quanto lo e' la timeline.
  const mailAttachmentsPanel = (
    <Accordion defaultExpanded disableGutters elevation={0} className="panel-box">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="relation-header">
          <Typography className="relation-title">
            {t('quotation.mailAttachmentsHeading')}
          </Typography>
          <span className="relation-count">{mailAttachments.length}</span>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="panel-box__details">
        {mailAttachments.length === 0 ? (
          <p className="text-muted text-sm">{t('quotation.noMailAttachments')}</p>
        ) : (
          mailAttachments.map(({ attachment, subject }) => (
            <div key={attachment.messageAttachmentId} className="doc-item">
              <span className="doc-emblem"><AttachFileIcon fontSize="small" /></span>
              <div className="doc-main">
                <div className="doc-name" title={attachment.fileName}>{attachment.fileName}</div>
                <div className="doc-meta" title={subject}>
                  {t('quotation.mailAttachmentFrom', {
                    subject,
                    size: formatFileSize(attachment.size),
                  })}
                </div>
              </div>
              <IconButton
                size="small"
                className="doc-del"
                aria-label={t('message.attachmentDownload')}
                onClick={() => downloadAttachment(attachment)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </div>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <div className="detail-page">
      {/* Breadcrumb fuori dalle colonne, come contatto e azienda. */}
      <nav className="detail-crumb" aria-label={t('quotation.title')}>
        <Link to="/quotations" className="detail-crumb__back">&larr; {t('quotation.title')}</Link>
        <span className="detail-crumb__sep">/</span>
        <span className="detail-crumb__current">{quotation.title}</span>
      </nav>

      {/* `--stack`: sotto i 1440px le tre colonne diventano due — l'anagrafica
          resta a sinistra della sua misura, il centro prende tutto il resto e la
          colonna destra si incolonna sotto (vedi _detail.scss). Vale anche per i
          contributi dei plugin negli slot `detail-right:top`/`:bottom`: stanno
          dentro `.detail-right`, quindi seguono la colonna senza saperlo. */}
      <div className="detail-layout detail-layout--stack">
      {/* LEFT — Identità + Info chiave + Note + Azioni */}
      <div className="detail-left">

        {/* Card identità: emblema documento, titolo serif, codice esterno in
            mono e riga stato+priorità. `.id-domain` è lo slot sotto il nome già
            usato dal dettaglio azienda per il dominio: qui porta il codice. */}
        <section className="card identity">
          <div className="id-head">
            <div className="avatar avatar--lg q-emblem"><QuotationsGlyph /></div>
            <div className="id-main">
              <div className="id-name">
                <EditableField
                  value={quotation.title}
                  variant={{ kind: 'text' }}
                  required
                  onCommit={commitField('title')}
                  label={t('quotation.columns.title')}
                />
              </div>
              {quotation.externalCode && <div className="id-domain">{quotation.externalCode}</div>}
              <div className="q-status-row">
                {/* Il chip NON è un doppione dello stato: è il campo stesso. La
                    classe della variante viene dalla mappa condivisa con la
                    lista, e il CSS traveste la view mode dell'EditableField da
                    pillola — doppio click apre il select come ovunque. */}
                <span className={`chip q-status ${quotationStatusChipClass(quotation.status)}`}>
                  <EditableField
                    value={quotation.status}
                    variant={{ kind: 'select', options: QUOTATION_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) }}
                    required
                    onCommit={commitField('status')}
                    label={t('quotation.statusLabel')}
                  />
                </span>
                {/* La chiave `priorityLabel` include gia' i due punti finali. */}
                <span className="q-prio">
                  {t('quotation.priorityLabel')}<b>{t('quotation.priorityDefault')}</b>
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="card panels">
          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="relation-title">{t('quotation.keyInfo')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {infoRows.map((row) => <div key={row.key}>{row.node}</div>)}
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="relation-title">{t('common.notes')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <EditableField
                value={quotation.notes}
                variant={{ kind: 'textarea', rows: 3 }}
                onCommit={commitField('notes')}
                label={t('common.notes')}
              />
            </AccordionDetails>
          </Accordion>
        </section>

        {/* Sotto l'anagrafica e prima delle azioni, come nelle schede contatto e azienda: sono
            riquadri di dati, non bottoni. */}
        {leftBottomContributors.map((c) => (
          <c.component key={c.key} entityId={quotation.quotationId} />
        ))}

        {/* Azioni rapide come pillole del mock, fuori dalle card. */}
        <div className="q-actions">
          <button
            type="button"
            className="q-btn q-btn--primary"
            onClick={attachDropzone.onClick}
            disabled={attachDropzone.uploading}
          >
            {t('quotation.uploadFile')}
          </button>
          <input
            ref={attachDropzone.inputRef}
            type="file"
            multiple
            className="visually-hidden"
            disabled={attachDropzone.uploading}
            onChange={attachDropzone.onInputChange}
          />
          {/* I contributi in `detail-left:after` stanno DENTRO la pila delle
              azioni, non sotto: sono altre azioni rapide (oggi "Carica File
              DXF" di Trentino), e da fuori non prendevano il gap del flex —
              risultavano staccate e di altezza diversa.

              Vengono PRIMA di "Aggiungi Nota" di proposito: in alto le azioni
              sul documento (carica un file, carica un DXF), in fondo quella
              sulla scheda. */}
          {leftAfterContributors.map((c) => (
            <c.component key={c.key} entityId={quotation.quotationId} />
          ))}

          <button type="button" className="q-btn q-btn--ghost" onClick={() => setAddNoteOpen(true)}>
            {t('quotation.addNote')}
          </button>

          {/* "Modifica" dietro lo stesso feature flag di contatti e aziende, ma
              qui porta alla PAGINA del form: il form del preventivo non è una
              modale, ha i sotto-form dei plugin e la cascata pipeline/stato.
              Al salvataggio si torna su questa scheda (vedi `exitTo` in
              QuotationForm), non sull'elenco. */}
          {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && (
            <button
              type="button"
              className="q-btn q-btn--primary"
              onClick={() => navigate(`/quotations/${quotation.quotationId}/edit`)}
            >
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>

      {/* CENTER — Attività, più le tab contribuite dai plugin. La striscia vive in
          DetailTabs (TS-107), condivisa con le schede contatto e azienda. */}
      <div className="detail-center">
        <DetailTabs
          entityType="Quotation"
          entityId={quotation.quotationId}
          coreLabel={t('quotation.tabs.activities')}
        >
          {activities.loading ? (
            <p className="text-muted">{t('quotation.loadingActivities')}</p>
          ) : (
            <ActivityTimeline
              items={activities.items}
              filters={activities.filters}
              section={section}
              onSectionChange={handleSectionChange}
              activityTabs={activityTabs}
              entityId={quotation.quotationId}
              onExpandItem={activities.onExpandItem}
              focusItemId={focusItemId}
              emptyMessage={t('quotation.emptyActivities')}
              searchable
              getCreateAction={(type) => {
                const taskType = ACTIVITY_TO_TASK_TYPE[type];
                if (!taskType) return null;
                return {
                  label: t(`activity.newItem.${type}`),
                  onClick: () => setQuickDrawer({ initialType: taskType }),
                };
              }}
            />
          )}
        </DetailTabs>
      </div>

      {/* RIGHT — Estensioni in cima, relazioni, documenti, chiusura */}
      <div className="detail-right">
        {rightTopContributors.map((c) => (
          <c.component key={c.key} entityId={quotation.quotationId} />
        ))}

        {/* "Contatti" è privo di ambiguità. "Aziende" invece SI SOVRAPPONE al
            campo Cliente a sinistra: il preventivo ha già `businessId` come FK
            obbligatoria, e le due rappresentazioni non si sincronizzano —
            cambiare il lookup non tocca la Relationship, cancellare la card non
            azzera `businessId`. Scelta consapevole: la FK resta l'unica
            autorità, queste card sono la vista del dato collegato, ed è da
            quell'insieme che il lookup pesca. */}
        {/* Ordine del mock: Documenti, poi Contatti, poi Aziende. Gli allegati delle
            email seguono i Documenti: sono file anche loro, ma non del preventivo. */}
        {documentsPanel}
        {mailAttachmentsPanel}

        <RelationPanels
          source={{ type: 'Quotation', id: quotation.quotationId }}
          targets={['Person', 'Business']}
        />

        {rightBottomContributors.map((c) => (
          <c.component key={c.key} entityId={quotation.quotationId} />
        ))}

        {/* Chiusura: importo in evidenza (cifra mono grande, come `.q-close`
            del mock) e azione di chiusura, nella stessa card. */}
        <section className="card q-close">
          {quotation.amount > 0 && (
            <>
              <div className="q-close__label">{t('quotation.amount')}</div>
              <div className="q-close__amount">{formatCurrency(quotation.amount)}</div>
            </>
          )}

          {/* "Contrassegna come completato": mappa su status 'accepted', che è il
              flusso chiuso positivo (l'enum non ha 'completed'). Disabilitato se
              già accettato, per evitare un no-op. */}
          <button
            type="button"
            className="q-btn q-btn--primary"
            disabled={quotation.status === 'accepted'}
            onClick={async () => {
              try {
                await dispatch(updateQuotation({
                  quotationId: quotation.quotationId,
                  status: 'accepted',
                })).unwrap();
                toast({ severity: 'success', message: t('quotation.markCompletedToast') });
              } catch (err) {
                toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
              }
            }}
          >
            {t('quotation.markCompleted')}
          </button>
        </section>
      </div>
      </div>

      {/* Creazione rapida di un task dai bottoni "+ Nuova X" delle sotto-tab:
          al salvataggio il task resta collegato al preventivo. */}
      {quickDrawer && (
        <TaskForm
          mode="modal"
          initialType={quickDrawer.initialType}
          linkedEntity={quotationFqn
            ? { className: quotationFqn, classPK: quotation.quotationId }
            : undefined
          }
          onClose={() => setQuickDrawer(null)}
          onCreated={() => {
            if (quotationFqn) {
              dispatch(fetchRelationships({ className: quotationFqn, classPK: quotation.quotationId }));
            }
          }}
        />
      )}

      {addNoteOpen && (
        <AddNoteDialog quotation={quotation} onClose={() => setAddNoteOpen(false)} />
      )}

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}
